import { Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { FlashService } from '../../../services/flash';
import { Session } from '../../../services/session';
import { LevelService } from '../../../services/level';
import { SubjectService } from '../../../services/subject';
import { AcademicYearService } from '../../../services/academic-year';
import { AcademicTermService } from '../../../services/academic-term';
import { IAcademicYear, IAcademicYears } from '../../../interfaces/academic-year';
import { IAcademicTerms } from '../../../interfaces/academic-term';
import { ILevels } from '../../../interfaces/level';
import { IGradeSystems, IScoreWeights } from '../../../interfaces/grade-system';
import { ICreateAssessmentConfigPayload } from '../../../interfaces/assessment';
import { AssessmentService } from '../../../services/assessment';
import { GradeSystemsService } from '../../../services/grade-systems';
import { LoadingIndicator } from '../../../shared/loading-indicator/loading-indicator';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';
import { StudentService } from '../../../services/student';
import { Helpers } from '../../../utilities/helpers';
import { DashboardService } from '../../../services/dashboard';

interface PageTab {
  label: string;
  route: string;
  roles: string[];
}

@Component({
  selector: 'app-academic-mgt',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LoadingIndicator,
    FormsModule,
    ShimmerEffect,
  ],
  templateUrl: './academic-mgt.html',
  styleUrl: './academic-mgt.css',
})
export class AcademicMgt implements OnInit {
  [x: string]: any;
  constructor(
    protected router: Router,
    private session: Session,
    private levelService: LevelService,
    private subjectService: SubjectService,
    private academicYearService: AcademicYearService,
    private academicTermService: AcademicTermService,
    private studentService: StudentService,
    private dashboardService: DashboardService,
    private assessmentService: AssessmentService,
    private gradeSystemsService: GradeSystemsService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole !== 'SUPER_ADMIN') {
      this.getDashboardData();
    }

    if (this.admins.includes(this.loggedUserRole ?? '')) {
      this.getAcademicYears();
    }
  }

  ngOnInit(): void {
    this.existingAcademicTerms = this.session.getItem('academicTerms');
  }

  protected search: string = '';
  protected schoolId?: string;
  protected loggedUserRole?: string;
  protected readonly flashService = inject(FlashService);
  protected status: string = '';
  protected addLevel = signal<boolean>(false);
  protected nameOfLevel!: string;
  protected levelCode!: string;
  protected levelSequence!: string;
  protected levelDescription!: string;
  protected addSubject = signal<boolean>(false);
  protected nameOfSubject!: string;
  protected subjectCode!: string;
  protected subjectDescription!: string;
  protected addAcademicYear = signal<boolean>(false);
  protected academicYearName = '';
  protected startDate = '';
  protected endDate = '';
  protected currentAcademicYear = false;
  protected addAcademicTerm = signal<boolean>(false);
  protected academicTermName = '';
  protected termStartDate = '';
  protected termEndDate = '';
  protected currentAcademicTerm = false;
  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicYearId: string = '';
  protected isEditing = signal<boolean>(false);
  protected isLoading = signal<boolean>(false);
  protected academicTermStatus: string = '';
  protected showAddStudent = signal<boolean>(false);
  protected admissionNumber!: string;
  protected firstName!: string;
  protected lastName!: string;
  protected sex: string = '';
  protected dob!: string;
  protected admissionDate!: string;
  protected parentName!: string;
  protected parentPhone!: string;
  protected parentEmail!: string;
  protected todayDate = new Date().toISOString().split('T')[0];
  protected emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  protected dashboardData = signal<any>(null);
  protected academicTerms = ['First Term', 'Second Term', 'Third Term'];
  protected academicYearStatus: string = '';
  protected academicYear = signal<IAcademicYear | null>(null);
  protected readonly Helpers = Helpers;
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected showTerms = signal(false);
  protected invalidStatuses = ['INACTIVE', 'COMPLETED', 'ARCHIVED'];
  protected configureTerm = signal<boolean>(false);
  protected assignScoreWeightId = '';
  protected assignGradingSystemId = '';
  protected assignAcademicYearId = '';
  protected assignTermId = '';
  protected assignLevelIds: string[] = [];
  protected scoreWeights = signal<IScoreWeights | null>(null);
  protected assignAcademicYears = signal<IAcademicYears | null>(null);
  protected assignAcademicTerms = signal<IAcademicTerms | null>(null);
  protected assignLevels = signal<ILevels | null>(null);
  protected isLoadingScoreWeights = signal<boolean>(false);
  protected isLoadingAssignAcademicYears = signal<boolean>(false);
  protected isLoadingAssignTerms = signal<boolean>(false);
  protected isLoadingAssignLevels = signal<boolean>(false);
  protected isAssigningToTerm = signal<boolean>(false);
  protected gradingSystems = signal<IGradeSystems | null>(null);

  protected isCurrentRoute(route: string): boolean {
    return this.router.url === route;
  }

  protected existingAcademicTerms: {
    term_name: string;
    academic_year_id: string;
  }[] = [];

  protected tabs: PageTab[] = [
    {
      label: 'LEVELS',
      route: 'levels',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'TEACHER', 'FINANCE_ADMIN'],
    },
    {
      label: 'ACADEMIC YEARS',
      route: 'academic-years',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
    {
      label: 'TERMS',
      route: 'terms',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
    {
      label: 'SUBJECTS',
      route: 'subjects',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'TEACHER'],
    },
    {
      label: 'STUDENTS',
      route: 'students',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN', 'TEACHER'],
    },
    {
      label: 'GRADE CONFIGURATION',
      route: 'grade-configuration',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
  ];

  protected visibleTabs(userRole: string): PageTab[] {
    return this.tabs.filter((link) => link.roles.includes(userRole));
  }

  protected createLevel(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating level. Try again.');
    }

    this.isEditing.set(true);

    const newLevelForm = {
      school_id: this.schoolId,
      name: this.nameOfLevel,
      code: this.levelCode,
      sequence: this.levelSequence,
      description: this.levelDescription,
    };

    this.levelService.createLevel(`${ApiEndpoints.url}/level`, newLevelForm).subscribe({
      next: () => {
        this.flashService.success('Level created successfully.');
        this.isEditing.set(false);
        this.addLevel.set(false);
        this.nameOfLevel = '';
        this.levelCode = '';
        this.levelSequence = '';
        this.levelDescription = '';
        window.location.reload();
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error creating level. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected createSubject(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating level. Try again.');
    }

    this.isEditing.set(true);

    const newSubjectForm = {
      school_id: this.schoolId,
      name: this.nameOfSubject,
      code: this.subjectCode,
      description: this.subjectDescription,
    };

    this.subjectService.createSubject(`${ApiEndpoints.url}/subject`, newSubjectForm).subscribe({
      next: () => {
        this.flashService.success('Subject created successfully.');
        this.isEditing.set(false);
        this.addSubject.set(false);
        window.location.reload();
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error creating subject. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected createAcademicYear(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (!this.schoolId) {
      void this.router.navigate(['/']);
      return;
    }

    this.isEditing.set(true);

    const academicYearForm = {
      school_id: this.schoolId,
      name: this.academicYearName,
      start_date: this.startDate,
      end_date: this.endDate,
      current_academic_year: this.currentAcademicYear,
      status: this.academicYearStatus,
    };

    this.academicYearService
      .createAcademicYear(`${ApiEndpoints.url}/academic-year`, academicYearForm)
      .subscribe({
        next: () => {
          this.isEditing.set(false);
          this.addAcademicYear.set(false);
          window.location.reload();
          this.flashService.success('Successfully added academic year.');
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not create academic year. Try again.',
          );
        },
      });
  }

  protected getAcademicYears(): void {
    this.isLoading.set(true);

    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected getAcademicYear(id: string): void {
    this.academicYearService
      .getAcademicYearById(`${ApiEndpoints.url}/academic-year/${id}`)
      .subscribe({
        next: (res) => {
          this.academicYear.set(res);
          this.showTerms.set(true);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Year. Refresh the page.',
          );
        },
      });
  }

  protected createAcademicTerm(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    this.isEditing.set(true);

    const academicTermForm = {
      school_id: this.schoolId,
      name: this.academicTermName,
      start_date: this.termStartDate,
      end_date: this.termEndDate,
      academic_year_id: this.academicYearId,
      current_term: this.currentAcademicTerm,
      status: this.academicTermStatus,
    };

    this.academicTermService
      .createAcademicTerm(`${ApiEndpoints.url}/academic-term`, academicTermForm)
      .subscribe({
        next: () => {
          this.isEditing.set(false);
          this.addAcademicTerm.set(false);
          window.location.reload();
          this.flashService.success('Successfully added academic term.');
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not create academic term. Try again.',
          );
        },
      });
  }

  protected addStudent(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (this.parentEmail && !this.emailPattern.test(this.parentEmail)) {
      this.flashService.failure('Enter a valid email address.');
      return;
    }

    this.isEditing.set(true);

    const studentForm = {
      school_id: this.schoolId,
      admission_number: this.admissionNumber,
      first_name: this.firstName,
      last_name: this.lastName,
      sex: this.sex,
      date_of_birth: this.dob,
      admission_date: this.admissionDate,
      parent_name: this.parentName,
      parent_phone: this.parentPhone,
      parent_email: this.parentEmail,
    };

    this.studentService.createStudent(`${ApiEndpoints.url}/student`, studentForm).subscribe({
      next: () => {
        this.flashService.success('Successfully added student.');
        this.isEditing.set(false);
        this.showAddStudent.set(false);
        window.location.reload();
      },
      error: (err) => {
        this.isEditing.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error adding student. Try again.');
      },
    });
  }

  protected openConfigureTerm(): void {
    this.assignScoreWeightId = '';
    this.assignGradingSystemId = '';
    this.assignAcademicYearId = '';
    this.assignTermId = '';
    this.assignLevelIds = [];
    this.assignAcademicTerms.set(null);
    this.configureTerm.set(true);

    this.getGradingSystems();
    this.getScoreWeights();
    this.getAssignableAcademicYears();
    this.getAssignableLevels();
  }

  protected closeConfigureTerm(): void {
    if (this.isAssigningToTerm()) {
      return;
    }

    this.configureTerm.set(false);
  }

  protected onAssignAcademicYearChange(academicYearId: string): void {
    this.assignTermId = '';
    this.assignAcademicTerms.set(null);

    if (!academicYearId) {
      return;
    }

    this.isLoadingAssignTerms.set(true);

    const params: Record<string, string | number> = {
      page: 1,
      size: 100,
      academic_year_id: academicYearId,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (data) => {
          const terms = data?.payload ?? [];

          this.assignAcademicTerms.set(data);
          this.isLoadingAssignTerms.set(false);

          const currentTerm = terms.find((term) => term.current_term);

          if (currentTerm) {
            this.assignTermId = currentTerm.id;
          }
        },
        error: (err) => {
          this.assignAcademicTerms.set(null);
          this.isLoadingAssignTerms.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load terms for the selected academic year.',
          );
        },
      });
  }

  protected get areAllAssignLevelsSelected(): boolean {
    const levels = this.assignLevels()?.payload ?? [];

    return levels.length > 0 && levels.every((level) => this.assignLevelIds.includes(level.id));
  }

  protected toggleAssignLevel(levelId: string): void {
    this.assignLevelIds = this.assignLevelIds.includes(levelId)
      ? this.assignLevelIds.filter((id) => id !== levelId)
      : [...this.assignLevelIds, levelId];
  }

  protected selectAllAssignLevels(): void {
    const levels = this.assignLevels()?.payload ?? [];

    this.assignLevelIds = this.areAllAssignLevelsSelected ? [] : levels.map((level) => level.id);
  }

  protected configureTermGradeSystem(form: NgForm): void {
    if (
      form.invalid ||
      !this.assignScoreWeightId ||
      !this.assignAcademicYearId ||
      !this.assignTermId ||
      !this.assignLevelIds.length
    ) {
      this.flashService.failure(
        'Select a score weight, academic year, term and at least one level',
      );
      return;
    }

    const scoreWeight = (this.scoreWeights()?.payload ?? []).find(
      (weight) => weight.id === this.assignScoreWeightId,
    );

    if (!scoreWeight || scoreWeight.status !== 'ACTIVE') {
      this.flashService.failure('Only active score weights can be assigned to a term');
      return;
    }

    if (this.loggedUserRole === 'SUPER_ADMIN' && !this.schoolId) {
      this.flashService.failure('School ID required');
      return;
    }

    this.isAssigningToTerm.set(true);

    const payload: ICreateAssessmentConfigPayload = {
      academic_year_id: this.assignAcademicYearId,
      term_id: this.assignTermId,
      level_ids: [...this.assignLevelIds],
      grading_config_id: this.assignScoreWeightId,
      grading_system_id: this.assignGradingSystemId
    };

    if (this.loggedUserRole === 'SUPER_ADMIN' && this.schoolId) {
      payload.school_id = this.schoolId;
    }

    this.assessmentService
      .bulkCreateAssessmentConfigs(`${ApiEndpoints.url}/assessment-config/bulk`, payload)
      .subscribe({
        next: () => {
          this.isAssigningToTerm.set(false);
          this.configureTerm.set(false);
          this.flashService.success('Term configured successfully');
        },
        error: (err) => {
          this.isAssigningToTerm.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not configure term. Try again.',
          );
        },
      });
  }

  private getScoreWeights(): void {
    if (this.scoreWeights() || this.isLoadingScoreWeights()) {
      return;
    }

    this.isLoadingScoreWeights.set(true);

    this.gradeSystemsService.getScoreWeights(`${ApiEndpoints.url}/grade-config`).subscribe({
      next: (data) => {
        this.scoreWeights.set(data);
        this.isLoadingScoreWeights.set(false);
      },
      error: (err) => {
        this.isLoadingScoreWeights.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load score weights. Try again.',
        );
      },
    });
  }

  protected getGradingSystems(): void {
    this.gradeSystemsService.getGradeSystems(`${ApiEndpoints.url}/grading-system`).subscribe({
      next: (data: IGradeSystems) => {
        this.gradingSystems.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load Grade Systems. Refresh the page',
        );
      },
    });
  }

  private getAssignableAcademicYears(): void {
    if (this.assignAcademicYears() || this.isLoadingAssignAcademicYears()) {
      return;
    }

    this.isLoadingAssignAcademicYears.set(true);

    const params: Record<string, string | number> = {
      page: 1,
      size: 100,
    };

    if (this.schoolId) {
      params['school_id'] = this.schoolId;
    }

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (data) => {
          this.assignAcademicYears.set(data);
          this.isLoadingAssignAcademicYears.set(false);
        },
        error: (err) => {
          this.isLoadingAssignAcademicYears.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load academic years. Try again.',
          );
        },
      });
  }

  private getAssignableLevels(): void {
    if (this.assignLevels() || this.isLoadingAssignLevels()) {
      return;
    }

    this.isLoadingAssignLevels.set(true);

    const params: Record<string, string | number> = {
      page: 1,
      size: 100,
    };

    if (this.schoolId) {
      params['school_id'] = this.schoolId;
    }

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (data) => {
        this.assignLevels.set(data);
        this.isLoadingAssignLevels.set(false);
      },
      error: (err) => {
        this.isLoadingAssignLevels.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Try again.');
      },
    });
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/academic-setup`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected termExists(termName: string): boolean {
    if (!this.academicYearId) {
      return false;
    }

    return this.existingAcademicTerms.some(
      (term) => term.academic_year_id === this.academicYearId && term.term_name === termName,
    );
  }
}
